import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { generateCustomerInsights } from '../../lib/openai';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';

const { FiUsers, FiLoader, FiCopy, FiCheck } = FiIcons;

function CustomerInsights({ customer, interactions = [] }) {
  const [insights, setInsights] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  const generateInsights = async () => {
    if (interactions.length === 0) {
      setInsights('No interactions available to analyze. Add customer interactions first.');
      return;
    }

    setIsLoading(true);
    try {
      const interactionsData = interactions.map(interaction => ({
        type: interaction.type,
        notes: interaction.notes,
        timestamp: interaction.timestamp
      }));
      
      const result = await generateCustomerInsights(interactionsData);
      setInsights(result);
    } catch (error) {
      console.error('Error generating customer insights:', error);
      setInsights('Failed to generate insights. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(insights);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="bg-white p-4 sm:p-6 rounded-lg shadow-sm border border-gray-200">
      <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
        <SafeIcon icon={FiUsers} className="w-5 h-5 mr-2 text-primary-500" />
        Customer Behavioral Insights
      </h3>
      <div className="space-y-4">
        <div className="bg-gray-50 p-3 rounded-lg mb-4">
          <div className="grid grid-cols-2 gap-2 text-sm">
            <p className="text-gray-500">Customer:</p>
            <p className="font-medium">{customer ? `${customer.firstName} ${customer.lastName}` : 'Not specified'}</p>
            <p className="text-gray-500">Status:</p>
            <p className="font-medium capitalize">{customer?.status || 'Not specified'}</p>
            <p className="text-gray-500">Interactions:</p>
            <p className="font-medium">{interactions.length} recorded</p>
          </div>
        </div>

        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={generateInsights}
          disabled={isLoading || interactions.length === 0}
          className="w-full bg-primary-600 text-white py-2 rounded-lg font-medium hover:bg-primary-700 transition-colors flex items-center justify-center space-x-2 disabled:opacity-50"
        >
          {isLoading ? (
            <>
              <SafeIcon icon={FiLoader} className="w-4 h-4 animate-spin" />
              <span>Analyzing...</span>
            </>
          ) : (
            <>
              <SafeIcon icon={FiUsers} className="w-4 h-4" />
              <span>Generate Customer Insights</span>
            </>
          )}
        </motion.button>

        {insights && (
          <div className="mt-4 border border-gray-200 rounded-lg p-4 bg-gray-50 relative">
            <pre className="whitespace-pre-wrap text-sm font-sans">{insights}</pre>
            <button
              onClick={copyToClipboard}
              className="absolute top-2 right-2 p-2 text-gray-500 hover:text-primary-600 hover:bg-gray-100 rounded-full transition-colors"
              title="Copy to clipboard"
            >
              <SafeIcon
                icon={copied ? FiCheck : FiCopy}
                className={`w-4 h-4 ${copied ? 'text-green-500' : ''}`}
              />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default CustomerInsights;